import { createWorker } from "tesseract.js";

export interface DatosComprobante {
  monto?: string;
  moneda?: string;
  referencia?: string;
  fecha?: string;
  banco?: string;
  metodo?: string;
}

const BANCOS = [
  "Banesco", "Mercantil", "Provincial", "Venezuela",
  "BNC", "Bicentenario", "Tesoro", "Exterior",
  "Caroní", "Sofitasa", "Plaza", "Fondo Común",
  "Bancamiga", "Banplus", "Bancaribe", "100%Banco",
  "Activo", "Nacional de Crédito", "Del Sur",
  "Bancrecer", "Mi Banco", "BOD",
];

export async function extraerDatosComprobante(file: File): Promise<DatosComprobante> {
  const worker = await createWorker("spa");
  try {
    const { data: { text } } = await worker.recognize(file);
    return parsearTexto(text);
  } finally {
    await worker.terminate();
  }
}

function parsearTexto(text: string): DatosComprobante {
  const result: DatosComprobante = {};
  const lines = text.replace(/\r/g, "").split("\n").map(l => l.trim()).filter(Boolean);
  const full = lines.join(" ");

  // Moneda — detectar del contexto
  if (/bs\.?s?\.?\s*[0-9]|[0-9].*\bbs\b|bol[ií]var/i.test(full)) {
    result.moneda = "Bs";
  } else if (/usd|\$\s*[0-9]|d[oó]lar/i.test(full)) {
    result.moneda = "USD";
  } else if (/eur|€/i.test(full)) {
    result.moneda = "EUR";
  } else if (/cop|peso/i.test(full)) {
    result.moneda = "COP";
  }

  // Monto
  const montoPatterns = [
    /(?:monto|amount|total|bs\.?|bsf\.?|bs\.?s\.?|usd|\$|€)\s*[:\s]*([0-9][0-9.,]*)/i,
    /([0-9]{1,3}(?:[.,][0-9]{3})*(?:[.,][0-9]{2}))\s*(?:bs|bsf|usd|\$)?/i,
  ];
  for (const p of montoPatterns) {
    const m = full.match(p);
    if (m) {
      let val = m[1].replace(/\s/g, "");
      if (val.includes(",") && val.indexOf(",") > val.lastIndexOf(".")) {
        val = val.replace(/\./g, "").replace(",", ".");
      } else if (val.includes(",") && !val.includes(".")) {
        val = val.replace(",", ".");
      }
      const num = parseFloat(val);
      if (num > 0 && num < 1_000_000_000) {
        result.monto = String(num);
        break;
      }
    }
  }

  // Referencia — número largo (6+ dígitos)
  const refPatterns = [
    /(?:referencia|ref\.?|nro\.?\s*(?:de\s*)?(?:ref|oper|trans)|comprobante|confirmaci[oó]n|n[uú]mero)\s*[:\s#]*([0-9]{4,20})/i,
    /\b([0-9]{6,20})\b/,
  ];
  for (const p of refPatterns) {
    const m = full.match(p);
    if (m) {
      result.referencia = m[1];
      break;
    }
  }

  // Fecha — DD/MM/YYYY, DD-MM-YYYY, YYYY-MM-DD
  const fechaPatterns = [
    /(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})/,
    /(\d{4})[/\-.](\d{1,2})[/\-.](\d{1,2})/,
  ];
  for (const p of fechaPatterns) {
    const m = full.match(p);
    if (m) {
      let [, a, b, c] = m;
      if (a.length === 4) {
        result.fecha = `${a}-${b.padStart(2, "0")}-${c.padStart(2, "0")}`;
      } else {
        result.fecha = `${c}-${b.padStart(2, "0")}-${a.padStart(2, "0")}`;
      }
      break;
    }
  }

  // Banco
  const upper = full.toUpperCase();
  for (const banco of BANCOS) {
    if (upper.includes(banco.toUpperCase())) {
      result.banco = banco;
      break;
    }
  }

  // Método de pago
  if (/pago\s*m[oó]vil/i.test(full) || /p2p/i.test(full)) {
    result.metodo = "Pago Móvil";
  } else if (/zelle/i.test(full)) {
    result.metodo = "Zelle";
  } else if (/transferencia/i.test(full)) {
    result.metodo = "Transferencia bancaria";
  } else if (/paypal/i.test(full)) {
    result.metodo = "PayPal";
  } else if (/dep[oó]sito/i.test(full)) {
    result.metodo = "Efectivo (depósito)";
  }

  return result;
}
